/**
 * The Jalali boundary. ADR-006 §4.
 *
 * JL-1: conversion happens in exactly two places — parsing at the API boundary
 * and formatting at the render boundary. Between them no Jalali value exists.
 * JL-3: exactly one Jalali library, imported **only** here. This is the file,
 * and the import below is the only one in the repository — enforced by CAL-L1
 * in `test/unit/enforcement.test.ts`, not by convention.
 *
 * JL-4: the leap rule is not hand-implemented. It is not a simple modulus, the
 * 33-year cycle has irregular exceptions, and a wrong Esfand 30 is a wrong
 * fiscal year end.
 *
 * JL-7: the version below is **exact**, not a range. The leap rule is a
 * correctness dependency, not a utility — a patch release that changes it moves
 * fiscal year boundaries. ADR-006 §11 gates any bump on re-running CAL-T2 and
 * CAL-T12.
 *
 * ## Official-calendar verification
 *
 * JL-8: CAL-T2 and CAL-T12 compare this library against the committed,
 * provenance-tagged 1400–1420 table in
 * `test/fixtures/jalali-golden-1400-1420.ts`. The expected values are
 * transcribed from the product-selected official sources; they are never
 * generated by the library under test.
 *
 * The self-consistency tests remain intentionally distinct: conversion
 * round-trips, and `isJalaliLeapYear` agrees with the length of Esfand and the
 * length of the year. Self-consistency alone cannot establish correctness: a
 * library coherently wrong about one year in the 33-year cycle silently moves
 * the fiscal year boundary by one day. The independent golden data makes that
 * failure observable before customer documents reach year-end close.
 */
import * as jalaali from 'jalaali-js';
import { businessDateOf, compareBusinessDates, partsOf, } from './business-date.js';
export class InvalidJalaliDate extends Error {
    constructor(value, reason) {
        super(`invalid Jalali date ${JSON.stringify(value)}: ${reason}`);
        this.name = 'InvalidJalaliDate';
    }
}
/** The library's supported range. Outside it the answers are extrapolation. */
export const MIN_JALALI_YEAR = jalaali.MIN_JALAALI_YEAR;
export const MAX_JALALI_YEAR = jalaali.MAX_JALAALI_YEAR;
function assertYearInRange(jy, subject) {
    if (!Number.isInteger(jy) || jy < MIN_JALALI_YEAR || jy > MAX_JALALI_YEAR) {
        throw new InvalidJalaliDate(subject, `year ${jy} is outside the supported range ${MIN_JALALI_YEAR}–${MAX_JALALI_YEAR}`);
    }
}
/** Gregorian business date to Jalali parts. JL-1's render boundary. */
export function toJalali(date) {
    const { year, month, day } = partsOf(date);
    const { jy, jm, jd } = jalaali.toJalaali(year, month, day);
    return { jy, jm, jd };
}
/** Jalali parts to a Gregorian business date. JL-1's parse boundary. */
export function fromJalali(jalali) {
    const { jy, jm, jd } = jalali;
    assertYearInRange(jy, jalali);
    if (!jalaali.isValidJalaaliDate(jy, jm, jd)) {
        // Deliberately specific: Esfand 30 in a common year is the single most
        // likely bad input, and "invalid date" alone would leave the caller
        // guessing which of the three fields was wrong.
        const reason = jm === 12 && jd === 30
            ? `Esfand has ${jalaliMonthLength(jy, 12)} days in ${jy} — it is not a leap year`
            : `no such date; month ${jm} of ${jy} has ${jm >= 1 && jm <= 12 ? jalaliMonthLength(jy, jm) : '—'} days`;
        throw new InvalidJalaliDate(jalali, reason);
    }
    const { gy, gm, gd } = jalaali.toGregorian(jy, jm, jd);
    return businessDateOf(gy, gm, gd);
}
/**
 * Whether a Jalali year is leap — whether Esfand has 30 days.
 *
 * Separated from the conversion because it is the value CAL-T2 and CAL-T12
 * check directly, and because it is the single number that silently moves a
 * fiscal year boundary. CAL-T2 and CAL-T12 compare it against independently
 * transcribed official golden data.
 */
export function isJalaliLeapYear(jy) {
    assertYearInRange(jy, jy);
    return jalaali.isLeapJalaaliYear(jy);
}
export function jalaliMonthLength(jy, jm) {
    assertYearInRange(jy, { jy, jm });
    if (!Number.isInteger(jm) || jm < 1 || jm > 12) {
        throw new InvalidJalaliDate({ jy, jm }, `month ${jm} is not 1-12`);
    }
    return jalaali.jalaaliMonthLength(jy, jm);
}
/**
 * SOT §12.5: a Jalali fiscal year splits into monthly periods. FY-4
 * (`fiscal.ts`) still applies here — the range need not start on Farvardin 1,
 * so the first period may be a short partial month, and correspondingly the
 * last period is whatever remains once full Jalali months are walked off the
 * middle. Every period boundary goes through `jalaliMonthLength`, never a
 * hand-counted 30/31, so a leap Esfand (JL-4) lands exactly where the
 * underlying library says it does.
 *
 * Returns Gregorian `BusinessDate` pairs only — per JL-1, no Jalali value
 * exists once this function returns; the caller (fiscal-period creation)
 * never sees a `JalaliDate`.
 */
export function jalaliMonthlyPeriods(startDate, endDate) {
    if (compareBusinessDates(startDate, endDate) > 0) {
        throw new InvalidJalaliDate({ startDate, endDate }, 'startDate must be on or before endDate');
    }
    const periods = [];
    let cursor = toJalali(startDate);
    for (;;) {
        const periodStart = fromJalali(cursor);
        const monthEnd = fromJalali({
            jy: cursor.jy,
            jm: cursor.jm,
            jd: jalaliMonthLength(cursor.jy, cursor.jm),
        });
        const periodEnd = compareBusinessDates(monthEnd, endDate) < 0 ? monthEnd : endDate;
        periods.push({ startDate: periodStart, endDate: periodEnd });
        if (compareBusinessDates(periodEnd, endDate) >= 0)
            break;
        cursor =
            cursor.jm === 12
                ? { jy: cursor.jy + 1, jm: 1, jd: 1 }
                : { jy: cursor.jy, jm: cursor.jm + 1, jd: 1 };
    }
    return periods;
}
/** Persian and Arabic-Indic digits, so pasted input is not rejected for its font. */
const DIGIT_MAP = new Map([
    ...'۰۱۲۳۴۵۶۷۸۹'.split('').map((d, i) => [d, String(i)]),
    ...'٠١٢٣٤٥٦٧٨٩'.split('').map((d, i) => [d, String(i)]),
]);
export function normalizeDigits(text) {
    return text.replace(/[۰-۹٠-٩]/g, (digit) => DIGIT_MAP.get(digit) ?? digit);
}
/**
 * AP-3: Jalali input is accepted at the boundary **in a declared format**,
 * converted immediately, and never stored as received.
 *
 * The declared format is `YYYY/MM/DD` or `YYYY-MM-DD`, in ASCII, Persian or
 * Arabic-Indic digits. Anything else is refused rather than guessed at — a
 * lenient parser is how `05/03` becomes the wrong month half the time.
 */
export function parseJalali(text) {
    const normalized = normalizeDigits(text.trim());
    const match = /^(\d{4})[/-](\d{1,2})[/-](\d{1,2})$/.exec(normalized);
    if (match === null) {
        throw new InvalidJalaliDate(text, 'expected YYYY/MM/DD or YYYY-MM-DD');
    }
    return fromJalali({
        jy: Number(match[1]),
        jm: Number(match[2]),
        jd: Number(match[3]),
    });
}
const ASCII_TO_PERSIAN = '۰۱۲۳۴۵۶۷۸۹';
/**
 * The render boundary, and nothing below it ever calls this (JL-2).
 *
 * Numeric only. The catalogue of display formats — short, long, with weekday —
 * is W0-19 with the design system (ADR-006 §12), and inventing one here would
 * be a design decision taken in a conversion module.
 */
export function formatJalali(date, options = {}) {
    const { separator = '/', persianDigits = false } = options;
    const { jy, jm, jd } = toJalali(date);
    const text = [
        String(jy).padStart(4, '0'),
        String(jm).padStart(2, '0'),
        String(jd).padStart(2, '0'),
    ].join(separator);
    return persianDigits
        ? text.replace(/\d/g, (digit) => ASCII_TO_PERSIAN[Number(digit)])
        : text;
}
//# sourceMappingURL=jalali.js.map